import React, {ChangeEvent, FC, useEffect, useRef, useState} from 'react';
import Api from "../../../../api";
import FullscreenModal from "components/FullscreenModal";
import styles from "./components.module.scss";
import MarkdownEditor from "../../../../components/MarkdownEditor/MarkdownEditor";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import clsx from "clsx";
import {SimpleModal} from "components/Modal";
import {RemoveContactInfoModal} from "../../../landing/SignUp2_Talent/components/RemoveContactinfoModal";
import {ChooseBio} from "../../../landing/SignUp2_Talent/components/ChooseBio";
import FixedLoader from "components/FixedLoader/FixedLoader";
import { WritingAssistFlow } from "components/WritingAssist";
import type { WritingAssistSaveSelection } from "components/WritingAssist";
import { isWritingAssistV2Enabled } from "utils/writingAssistV2";


export interface CoverLetterProps {
    coverLetter?: string,
    name?: string,
    jobTitle?: string,  // NEW: Add job context
    practiceName?: string,
    jobId?: number,
    isOpen: boolean,
    onClose: () => void,
    onSaveCoverLetter: (coverLetter: string, saveAsBase?: boolean) => void,
    onSaveWritingAssistCoverLetter?: (selection: WritingAssistSaveSelection) => Promise<unknown>,
    onWritingAssistSaveError?: (error: unknown) => void
}

interface CheckTextResponse {
    spellChecked: string;
    enhanced: string;
    error?: boolean; 
    message?: string;
}

const MIN_WORDS = 10;

const countWords = (inputString: string): number => {
    const trimmed = inputString.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
}

const tryLaterText = "Sorry, you've used all of your profile assists for today. Please skip or try again tomorrow."

const LegacyCoverLetter: FC<CoverLetterProps> = (
    {
        coverLetter,
        name,
        jobTitle,
        isOpen,
        onClose,
        onSaveCoverLetter
    }) => {
    const [inputValue, setInputValue] = useState(coverLetter || '');
    const [spellCheckedResponse, setSpellCheckedResponse] = useState('');
    const [enhancedResponse, setEnhancedResponse] = useState('');
    const [removeContactInfoModal, setRemoveContactInfoModal] = useState(false);
    const [isChooseBioOpen, setIsChooseBioOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [aboutError, setAboutError] = useState<string>();



    const onSubmit = async (event: any) => {
        event.preventDefault();
        if (countWords(inputValue) < MIN_WORDS) {
            setAboutError('Please write a bit more.');
            return;
        }
        setAboutError(undefined);
        try {
            setLoading(true);
            const message: CheckTextResponse = await Api.checkTextContactInfo(inputValue, 'coverLetter', true);
            if (message?.error) {
                setAboutError(message?.message || 'An error occurred. Please try again later.');
            } else {
                setSpellCheckedResponse(message.spellChecked);
                setEnhancedResponse(message.enhanced);
                setIsChooseBioOpen(true);
            }
        } catch (e: any) {
            console.error('There was an error!', e);
            let errorMsg = 'An unexpected error occurred. Please try again later.';
            try {
                const parsed = typeof e?.message === 'string' ? JSON.parse(e.message) : null;
                if (parsed?.message) errorMsg = parsed.message;
            } catch { /* keep generic message */ }
            setAboutError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setInputValue(coverLetter || '');
    }, [coverLetter]);

    const handleInputChange = (value: string) => {
        setInputValue(value);
    }

    return (
        <>
            {isChooseBioOpen ? <ChooseBio
                isOpen={isChooseBioOpen}
                onBack={() => setIsChooseBioOpen(false)}
                spellCheckedResponse={spellCheckedResponse}
                enhancedResponse={enhancedResponse}
                onSave={(text: string, saveAsBase?: boolean) => {  // MODIFIED
                    onSaveCoverLetter(text, saveAsBase);
                    onClose();
                }}
                removeContactInfoModal={removeContactInfoModal}
                setRemoveContactInfoModal={setRemoveContactInfoModal}
                rewritesLeft={5}  // Always show 5 for job applications (no limit)
                isCoverLetter={true}
                isJobApplication={true}  // NEW: Flag to enable save as base radio buttons
            /> : null}
            <FullscreenModal
                title="Cover Letter"
                isOpen={isOpen && !isChooseBioOpen}
                buttonText={'Review & Save'}
                onClose={onClose}
                onSubmit={onSubmit}
                stickyBottom={false}
                disabledSubmit={countWords(inputValue) < MIN_WORDS}
            >
                <SimpleModal isOpen={removeContactInfoModal}>
                    <RemoveContactInfoModal onCloseButtonClick={() => setRemoveContactInfoModal(false)}/>
                </SimpleModal>

                <div className={styles.minimalContainer}>
                    {/* Title Section */}
                    <div className={styles.titleSection}>
                        <h1 className={styles.mainTitle}>{jobTitle ? `Cover Letter for ${jobTitle}` : 'Base Cover Letter'}</h1>
                        <p className={styles.greetingText}>Hey {name}! 👋</p>
                    </div>

                    {/* Instructions Card */}
                    <div className={styles.instructionsCard}>
                        <h2>Time to write a stellar cover letter!</h2>
                        <h3>If you need some expert advice, check out our article: <br></br><a href="https://support.tempstars.com/hc/en-us/articles/43312869820307-Writing-a-Compelling-Cover-Letter-for-TempStars-Job-Board-Land-Your-Dream-Job" target="_blank" rel="noopener noreferrer" className="external">Write a Compelling Cover letter and Land Your Dream Job</a></h3>
                        <p>💡 Tip: Write in clean paragraphs. Line breaks and indentation are preserved.</p>
                    </div>

                    {/* Editor Section */}
                    <div className={styles.editorSectionMinimal}>
                        <div className={styles.editorCardMinimal}>
                            <div className={styles.editorContent}>
                                <MarkdownEditor
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    placeholder="Introduce yourself and highlight your skills..."
                                    label=""
                                    error={!!aboutError}
                                    helperText={aboutError}
                                    rows={14}
                                    mode={'plain'}
                                />
                                {(() => {
                                    const words = countWords(inputValue);
                                    const metMin = words >= MIN_WORDS;
                                    return (
                                        <div
                                            className={clsx(
                                                styles.wordCountFooter,
                                                metMin ? styles.wordCountMet : styles.wordCountBelow
                                            )}
                                            aria-live="polite"
                                        >
                                            {metMin
                                                ? `${words} words`
                                                : `${words} / ${MIN_WORDS} min words`}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Privacy Note */}
                    <div className={styles.privacyNoteMinimal}>
                        <p>
                            <strong>Note:</strong> Don't include contact information (email, phone, etc.) in this section. It gets removed automatically.
                        </p>
                    </div>

                    {/* Help Section */}
                    <div className={styles.helpSectionMinimal}>
                        <div className={styles.helpIcon}>
                            <InfoOutlinedIcon />
                        </div>
                        <div className={styles.helpTextContent}>
                            <h3>Need help writing?</h3>
                            <p>
                                When you hit "Review & Save" we'll fix spelling & punctuation and show an optional refined version. Output stays plain text with paragraphs.
                            </p>

                        </div>
                    </div>
                </div>
            </FullscreenModal>
            <FixedLoader
                loading={loading}
            />
        </>
    );
}

const WritingAssistApplicationCoverLetter: FC<CoverLetterProps> = ({
    coverLetter,
    practiceName,
    jobId,
    isOpen,
    onClose,
    onSaveWritingAssistCoverLetter,
    onWritingAssistSaveError,
}) => {
    if (!jobId || !onSaveWritingAssistCoverLetter) return null;
    return (
        <FullscreenModal
            title="Cover Letter"
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={(event) => event.preventDefault()}
            stickyBottom={false}
        >
            <div data-writing-assist-version="v2">
                <WritingAssistFlow
                    context="applicationCoverLetter"
                    practiceName={practiceName || 'this practice'}
                    entityId={jobId}
                    initialText={coverLetter || ''}
                    save={onSaveWritingAssistCoverLetter}
                    onSaveError={onWritingAssistSaveError}
                />
            </div>
        </FullscreenModal>
    );
};

export const CoverLetter: FC<CoverLetterProps> = (props) => (
    isWritingAssistV2Enabled()
        ? <WritingAssistApplicationCoverLetter {...props} />
        : <LegacyCoverLetter {...props} />
);
